import { Fragment, useId } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Area, AreaChart } from "recharts";

import { Card, CardContent } from "@/components/ui/card";
import { type ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard, LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { ConsumerResearchOverviewStatsData } from "@/schema/brand-os/ai-consumer-research";

const sparklineConfig = {
  value: { label: "Value", color: "hsl(var(--muted-foreground))" },
} satisfies ChartConfig;

interface MetricCardConfig {
  title: string;
  value: string;
  rawValue: number;
}

function cardConfig(stats: ConsumerResearchOverviewStatsData): MetricCardConfig[] {
  const { total_mentions, avg_sentiment, share_of_voice, brand_nps } = stats;

  return [
    { title: "Total Mentions", rawValue: total_mentions, value: new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(total_mentions) },
    { title: "Avg Sentiment", rawValue: avg_sentiment, value: `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(avg_sentiment)}%` },
    { title: "Share of Voice", rawValue: share_of_voice, value: `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(share_of_voice)}%` },
    { title: "Brand NPS", rawValue: brand_nps, value: new Intl.NumberFormat("en-US").format(brand_nps) },
  ];
}

function MetricsComponent({ status, stats }: { status: QueryStatus; stats?: ConsumerResearchOverviewStatsData | null }) {
  switch (status) {
    case "pending":
      return <MetricsSkeleton />;
    case "error":
      return <LockedCard message={message} className="w-full" />;
    case "success":
      return stats ? <MetricCard stats={stats} /> : <LockedCard className="w-full" message={message} />;
  }
}

export function ConsumerBrandMetrics() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewStats());

  return (
    <div className="flex items-center overflow-x-auto scrollbar-hidden gap-6">
      <MetricsComponent status={status} stats={data} />
    </div>
  );
}

function MetricCard({ stats }: { stats: ConsumerResearchOverviewStatsData }) {
  const cards = cardConfig(stats);
  const isValueValid = cards.some((card) => card.rawValue !== 0);

  return (
    <Fragment>
      {cards.map((card) => (
        <Card key={card.title} className="flex flex-col gap-3 py-5 min-w-64 flex-1 basis-0">
          <CardContent className="flex flex-col gap-3 px-5 pt-0">
            <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
            {isValueValid ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-2xl font-semibold tabular-nums tracking-tight">{card.value}</p>
                <div className="flex items-center gap-4">
                  <p className="text-xs text-muted-foreground">0% change</p>
                  <p className="text-xs text-muted-foreground">vs last period</p>
                </div>
                <FlatSparkline />
              </div>
            ) : (
              <LockedContent message={message} />
            )}
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}

function FlatSparkline() {
  const gradientId = useId().replace(/:/g, "");

  return (
    <ChartContainer config={sparklineConfig} className="h-8 w-full min-w-0">
      <AreaChart data={Array.from({ length: 12 }, () => ({ value: 1 }))} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-value)" stopOpacity={0.16} />
            <stop offset="100%" stopColor="var(--color-value)" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <Area dataKey="value" type="linear" stroke="var(--color-value)" strokeWidth={1.5} fill={`url(#${gradientId})`} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ChartContainer>
  );
}

function MetricsSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i} className="flex flex-col gap-3 py-5 min-w-64 flex-1 basis-0">
          <CardContent className="flex flex-col gap-3 px-5 pt-0">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-14 rounded-md" />
              <Skeleton className="h-3 w-20 rounded-md" />
            </div>
            <div className="opacity-30">
              <FlatSparkline />
            </div>
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}
